import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type Approval } from '../api/approvalRequests';
import { cdnApi } from '../api/cdn';
import { CDNUpload } from '../types';

export const useCDNFiles = (prefix: string = '') => {
  return useQuery({
    queryKey: ['cdn', 'files', prefix],
    queryFn: () => cdnApi.listFiles(prefix),
    staleTime: 30000,
  });
};

export const useCDNVersion = () => {
  return useQuery({
    queryKey: ['cdn', 'version'],
    queryFn: () => cdnApi.getVersionInfo(),
    refetchInterval: 60000, // Check for new versions every minute
  });
};

export const useCDNStats = () => {
  return useQuery({
    queryKey: ['cdn', 'stats'],
    queryFn: () => cdnApi.getStats(),
    staleTime: 60000,
  });
};

export const useCDNFilesByCategory = () => {
  return useQuery({
    queryKey: ['cdn', 'files', 'by-category'],
    queryFn: () => cdnApi.getFilesByCategory(),
    staleTime: 30000,
  });
};

export const useUploadFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (upload: CDNUpload) => cdnApi.uploadFile(upload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cdn', 'files'] });
      queryClient.invalidateQueries({ queryKey: ['cdn', 'stats'] });
    },
  });
};

export const useDeleteFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, approval }: { key: string; approval: Approval }) =>
      cdnApi.deleteFile(key, approval),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cdn', 'files'] });
      queryClient.invalidateQueries({ queryKey: ['cdn', 'stats'] });
    },
  });
};
